import RATINGS, { RatingConfig } from "constants/ratings";
import {
  InfieldPositions,
  IPlayer,
  OutfieldPositions,
  PitcherPositions,
  PlayerTypes,
  Positions,
  Teams,
} from "types/Player";
import { IRosterSkill, SkillLevel, SkillType } from "types/Skill";
import ITrainer, { TrainerTypes } from "types/Trainer";
import Utils from ".";
import Rarity from "../types/Rarity";
import { jsonEvents, jsonSkills, SkillNames } from "./json";

const getSkillRating = (rarity: Rarity, level: SkillLevel, resolve?: boolean) => {
  if (resolve) {
    return RATINGS.resolve[level - 1];
  }

  return RATINGS.resolve[rarity][level - 1];
};

export type RatedDeckData = {
  skills: IRosterSkill[];
  events: (keyof typeof jsonEvents)[];
  teamBonus: string[];
  rarities: Rarity[];
  rating: {
    skills: number;
    events: number;
    teamBonus: number;
    rarities: number;
    statSynergy: number;
    total: number;
  };
  position?: Positions;
  team?: Teams;
};

const isSkillValid = (skillName: SkillNames, position: Positions | "any", any = false) => {
  const isResolve = skillName.includes("Resolve");

  if (isResolve) {
    if (any) {
      return false;
    }

    const [resolvePosition] = skillName.split(`'`);

    if (resolvePosition === "MRP") {
      return position === PitcherPositions.RP;
    }

    return position === resolvePosition;
  }

  if (Object.keys(PitcherPositions).includes(position)) {
    return true;
  }

  const isCatcher = ["Framing"].includes(skillName);

  if (isCatcher && position !== InfieldPositions.C) {
    return false;
  }

  const isOutfielder = skillName.includes("OF");

  if (isOutfielder) {
    if (any) {
      return false;
    }

    return Object.keys(OutfieldPositions).includes(position);
  }

  const isInfielder = skillName.includes("IF");

  if (isInfielder) {
    if (any) {
      return false;
    }

    return Object.keys(InfieldPositions).includes(position);
  }

  return true;
};

const rateSkill = (skill: IRosterSkill) => {
  if (skill.name.toLowerCase().includes("resolve")) {
    return RATINGS.resolve[skill.level];
  } else {
    return RATINGS.skills[skill.skillGrade][skill.level];
  }
};

const rateDeck = (deck: ITrainer[], _player: IPlayer, config: RatingConfig): RatedDeckData => {
  console.log("###########################");

  console.log("[Rating Deck] Trainers: ", deck.map((t) => t.name).join(", "));

  const skills: RatedDeckData["skills"] = [];
  const events: RatedDeckData["events"] = [];
  const teamBonus: RatedDeckData["teamBonus"] = [];
  const rarities: RatedDeckData["rarities"] = [];
  const statTypes: { [key in TrainerTypes]?: number } = {};
  const rating: RatedDeckData["rating"] = {
    skills: 0,
    events: 0,
    teamBonus: 0,
    rarities: 0,
    statSynergy: 0,
    total: 0,
  };

  const player = { ..._player };

  let bestPosition = undefined;
  let bestTeam = undefined;

  console.log("[Rating Deck] Calculating best team");

  if ((!player.team || player.team === "any") && deck.length > 0) {
    const teams: Teams[] = deck.flatMap((trainer) => trainer.bonusTeam || []);
    const teamsCount = Utils.countOcurrences(teams);

    if (!teamsCount) {
      console.log("teamsCount", JSON.stringify(teamsCount));
    }
    const sortedTeams = Object.entries(teamsCount).sort((a, b) => b[1] - a[1]);

    bestTeam = sortedTeams.length && (sortedTeams[0][0] as Teams);

    player.team = bestTeam || "any";
  }

  console.log("[Rating Deck] Calculating best position");

  if ((!player.position || player.position === "any") && deck.length > 0) {
    const positions: Positions[] = deck.flatMap((trainer) => trainer.position);

    const positionsCounts = Utils.countOcurrences(positions);

    if (!positionsCounts) {
      console.log("sortedPositions", JSON.stringify(positionsCounts));
    }
    const sortedPositions = Object.entries(positionsCounts).sort((a, b) => b[1] - a[1]);

    bestPosition = sortedPositions[0][0] as Positions;

    player.position = bestPosition;
  }

  console.log("[Rating Deck] Initializing skills");

  const skillGroups: { [key in SkillType]?: { [key in SkillNames]?: SkillLevel } } = {};

  if (player.positionType === "pitcher") {
    skillGroups[SkillType["Pitch Type I"]] = {
      four_seam: 1,
    };
  }

  const trainerNames = deck.map((trainer) => trainer.name);

  for (const trainer of deck) {
    console.log(`[Rating Deck] Processing ${trainer.name}`);

    const hasPosition = player.position && player.position !== "any";

    //Stat Synergy
    for (const type of trainer.type) {
      if (![TrainerTypes.GP, TrainerTypes.SP].includes(type)) {
        statTypes[type] = statTypes[type] || 0;
        statTypes[type] += 1;
      }
    }

    //Skills
    const levelSkills = trainer.skills[trainer.level];

    if (!levelSkills) {
      console.log("levelSkills", trainer.name, trainer.level);
    }
    for (const [name, level] of Object.entries(levelSkills)) {
      const skill = jsonSkills[name as keyof typeof jsonSkills];

      const isValid = !hasPosition || isSkillValid(skill.name as SkillNames, player.position);

      if (!isValid) {
        continue;
      }

      if (!skillGroups[skill.type]) {
        skillGroups[skill.type] = {};
      }

      const group = skillGroups[skill.type];

      group[name] = group[name] || 0;
      group[name] += level;

      if (group[name] > 5) {
        group[name] = 5;
      }
    }

    //Events
    if (Array.isArray(trainer.comboEvents)) {
      for (const event of trainer.comboEvents) {
        if (events.includes(event)) {
          continue;
        }

        const jsonEvent = jsonEvents[event as keyof typeof jsonEvents];

        const isValid = jsonEvent.requiredTrainers.every((name) => trainerNames.includes(name));

        if (isValid) {
          events.push(event);
        }
      }
    }

    //Team Bonus
    const trainerTeams = trainer.bonusTeam || [];
    if (trainerTeams.includes(player.team)) {
      teamBonus.push(trainer.name);
    }

    //Rarities
    rarities.push(trainer.rarity as Rarity);
  }

  if (!skillGroups) {
    console.log("skillGroups", JSON.stringify(skillGroups));
  }

  console.log("[Rating Deck] Processing skills");

  for (const [group, groupSkills] of Object.entries(skillGroups)) {
    if (!groupSkills) {
      console.log("parsedSkills", group, JSON.stringify(groupSkills));
    }

    const parsedSkills = Object.entries(groupSkills).map(
      ([skillName, skillLevel]): IRosterSkill => {
        const jsonSkill = jsonSkills[skillName as SkillNames];

        const isResolve = skillName.toLowerCase().includes("resolve");

        const rating: number = isResolve
          ? Utils.getFirstValid(config.resolve?.[skillLevel - 1], RATINGS.resolve[skillLevel - 1])
          : Utils.getFirstValid(
              config.skills?.[jsonSkill.skillGrade]?.[skillLevel - 1],
              RATINGS.skills[jsonSkill.skillGrade][skillLevel - 1]
            );

        return { ...jsonSkill, level: skillLevel, rating };
      }
    );

    const sortedSkills = parsedSkills.sort((a, b) => b.rating - a.rating);

    if (group === SkillType.Common) {
      // Max 20 skills
      const diff = 20 - Object.keys(skillGroups).length;

      const bestSkills = sortedSkills.slice(0, diff + 1);

      const _rating = bestSkills.reduce((accum, curr) => {
        return accum + curr.rating;
      }, 0);

      rating.skills += _rating;

      skills.push(...bestSkills);
      continue;
    }

    rating.skills += sortedSkills[0].rating;

    skills.push(sortedSkills[0]);
  }

  console.log("[Rating Deck] Calculating ratings");

  // Rate everything except skills
  const eventRating = config.comboEvent || RATINGS.comboEvent;
  rating.events += events.length * eventRating;

  const teamBonusRating = config.bonusTeam || RATINGS.bonusTeam;
  rating.teamBonus += teamBonus.length * teamBonusRating;

  for (const rarity of rarities) {
    rating.rarities += Utils.getFirstValid(config.rarities?.[rarity], RATINGS.rarities[rarity]);
  }

  if (!statTypes) {
    console.log("statTypes", JSON.stringify(statTypes));
  }
  const sortedTypes = Object.entries(statTypes).sort((a, b) => b[1] - a[1]);

  const [firstType, secondType] = sortedTypes;

  if (secondType && secondType[1] >= 2) {
    if (firstType[1] - 2 <= secondType[1]) {
      const totalSynergy = firstType[1] + secondType[1];

      const statSynergyRating = config.statSynergy || RATINGS.statSynergy;
      rating.statSynergy = totalSynergy * statSynergyRating;
    }
  }

  rating.total = rating.events + rating.teamBonus + rating.rarities + rating.skills + rating.statSynergy;

  console.log("[Rating Deck] Finished");

  return {
    skills,
    events,
    teamBonus,
    rarities,
    rating,
    position: bestPosition,
    team: bestTeam,
  };
};

export type RatedDeck = {
  deck: ITrainer[];
  data: RatedDeckData;
};

const rateDecks = (trainers: ITrainer[], player: IPlayer, config: RatingConfig): RatedDeck[] => {
  const decks = [...Utils.combine(trainers, 6)];

  const ratedDecks = decks.map((deck) => ({ deck, data: rateDeck(deck, player, config) }));

  const sortedDecks = ratedDecks.sort((a, b) => b.data.rating.total - a.data.rating.total);

  return sortedDecks;
};

const RatingUtils = {
  rateDeck,
  getSkillRating,
  rateDecks,
  rateSkill,
};

export default RatingUtils;
